import './styles/main.scss';
import { TourViewer } from '@/components/viewer';
import { AppHeader } from '@/components/header';
import { mountStartScreen, shouldShowStartScreen } from '@/components/start-screen';
import { Sidebar } from '@/components/sidebar';
import { InfoOverlay } from '@/components/overlay';
import { applyTheme, loadTour, NO_PROJECT_SELECTED } from '@/core/tour-loader';
import { currentBuildingId, currentFloorId, currentRoomId } from '@/core/state';
import { findRoomLocation, firstRoomLocation } from '@/core/tour-utils';
import { VIEWER_DEFAULTS } from '@/core/config';
import { detectEmbedMode, mountEmbedViewer } from '@/components/embed';
import type { Tour } from '@/types/tour';

interface Slots {
  headerEl: HTMLElement;
  sidebarEl: HTMLElement;
  viewerEl: HTMLElement;
}

function mountSkeleton(root: HTMLElement): Slots {
  root.innerHTML = `
    <header class="app-header" role="banner"></header>
    <aside class="app-sidebar" role="navigation" aria-label="Tour-Navigation"></aside>
    <section class="app-viewer" id="viewer" aria-label="360-Grad-Panorama"></section>
  `;
  const headerEl = root.querySelector<HTMLElement>('.app-header');
  const sidebarEl = root.querySelector<HTMLElement>('.app-sidebar');
  const viewerEl = root.querySelector<HTMLElement>('#viewer');
  if (!headerEl || !sidebarEl || !viewerEl) {
    throw new Error('App-Skeleton konnte nicht erstellt werden');
  }
  return { headerEl, sidebarEl, viewerEl };
}

function showFatalError(root: HTMLElement, message: string): void {
  root.innerHTML = `
    <div class="fatal-error" role="alert">
      <h1>Tour konnte nicht geladen werden</h1>
      <p>${escapeText(message)}</p>
    </div>
  `;
}

function escapeText(s: string): string {
  return s.replace(/[&<>"']/g, (c) => {
    const map: Record<string, string> = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    };
    return map[c] ?? c;
  });
}

function startViewer(tour: Tour, root: HTMLElement): void {
  const start = (tour.defaultRoomId ? findRoomLocation(tour, tour.defaultRoomId) : undefined)
    ?? firstRoomLocation(tour);
  if (!start) {
    root.innerHTML = `
      <div class="fatal-error" role="alert">
        <h1>Leere Tour</h1>
        <p>Diese Tour enthaelt noch keine Raeume.</p>
        <p class="fatal-error__hint">
          Oeffne die Tour mit <code>?edit=1</code>, um Raeume anzulegen.
        </p>
      </div>
    `;
    return;
  }

  const { headerEl, sidebarEl, viewerEl } = mountSkeleton(root);

  const viewer = new TourViewer(viewerEl, {
    pano: start.room.pano,
    yaw: start.room.initialYaw,
    pitch: start.room.initialPitch ?? VIEWER_DEFAULTS.defaultPitch,
    zoom: start.room.initialZoom ?? VIEWER_DEFAULTS.defaultZoom,
  });

  new AppHeader(headerEl, tour);
  new Sidebar(sidebarEl, tour);
  new InfoOverlay(viewerEl, tour);

  let isFirstRoomSet = true;
  currentRoomId.subscribe((roomId) => {
    if (isFirstRoomSet) {
      isFirstRoomSet = false;
      return;
    }
    const loc = findRoomLocation(tour, roomId);
    if (!loc) return;
    void viewer.loadPanorama({
      pano: loc.room.pano,
      yaw: loc.room.initialYaw,
      pitch: loc.room.initialPitch ?? VIEWER_DEFAULTS.defaultPitch,
      zoom: loc.room.initialZoom ?? VIEWER_DEFAULTS.defaultZoom,
    });
  });

  currentBuildingId.set(start.building.id);
  currentFloorId.set(start.floor.id);
  currentRoomId.set(start.room.id);
}

function isEditorMode(): boolean {
  const params = new URLSearchParams(window.location.search);
  return params.get('edit') === '1';
}

function hasProjectParam(): boolean {
  return !!new URLSearchParams(window.location.search).get('project');
}

async function bootstrap(): Promise<void> {
  const root = document.getElementById('app');
  if (!root) throw new Error('App-Root #app fehlt');

  const editor = isEditorMode();
  const projectParam = hasProjectParam();
  const embed = detectEmbedMode();

  // Embed-Modus (?embed=1) laeuft am Editor-Bundle vorbei — wir wollen im
  // iframe weder Login-UI noch das schwerere Editor-Bundle. Tour wird per
  // tour.php geladen (funktioniert mit ?project=…, Auth via meta.public).
  if (embed.mode !== 'none' && projectParam) {
    try {
      const { tour } = await loadTour();
      applyTheme(tour.theme);
      mountEmbedViewer(root, tour, { mode: embed.mode, roomId: embed.roomId });
    } catch (err) {
      showFatalError(root, err instanceof Error ? err.message : String(err));
    }
    return;
  }

  // Builder-Routen (?edit=1 oder ?project=…) laufen ueber den dynamischen
  // Editor-Bundle-Import. Der haendelt Auth, Projekt-Auswahl und Editor.
  if (editor || projectParam) {
    try {
      const { mountBuilder } = await import('@/editor/editor');
      await mountBuilder(root);
    } catch (err) {
      console.error('Builder-Bootstrap fehlgeschlagen:', err);
      showFatalError(root, err instanceof Error ? err.message : String(err));
    }
    return;
  }

  // Customer-Modus: keine API, keine Auth, statisches /tour.json + assets.
  try {
    const { tour } = await loadTour();
    applyTheme(tour.theme);
    if (shouldShowStartScreen(tour)) {
      mountStartScreen(root, tour, () => {
        root.innerHTML = '';
        startViewer(tour, root);
      });
    } else {
      startViewer(tour, root);
    }
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    if (msg === NO_PROJECT_SELECTED) {
      // Wir sind in einer Builder-Installation, aber ohne Projekt-Param —
      // schicke den User in den Editor-Modus.
      window.location.search = '?edit=1';
      return;
    }
    showFatalError(root, msg);
  }
}

void bootstrap();
